import { computed } from 'vue'
import {
    Order, OrderRequest, Product, Table, User, UserService, OrderService, TableSessionService,
    OrderFlagType, OrderStatusType, CustomPaymentTypeLabel,
} from 'app-core'

/**
 * Asked before the claim when the request's table is another waiter's (see
 * `approvalConsentOwner`). Resolving false leaves the request pending and
 * untouched on the backend. Required, so a caller without a UI has to say
 * `async () => true` out loud rather than skip the question by omission.
 */
export type ConfirmForeignTable = (owner: User, tableName: string) => Promise<boolean>

export function useOrderRequests() {

    // The hub shows only requests still awaiting action. Approved/rejected
    // requests persist on the backend (recoverability + guest visibility) but
    // are filtered out here; the snapshot GC prunes the local rows.
    const pendingOrderRequests = computed(() =>
        (OrderRequest.all() as OrderRequest[]).filter(r => r.isPending()))

    /** Reflect a handled request locally and tell peers to drop it from their hub. */
    function settleLocally(request: OrderRequest) {
        request.$save()
        OrderRequest.syncTerminals({
            items: [{ action: 'patch', record: request.$toJson() }]
        })
    }

    /**
     * The waiter serving the request's table when that is someone other than
     * the current user — the owner of the table's open rounds. Shown on the
     * hub card and detail so the approver knows whose table the round lands on.
     */
    function otherTableWaiter(request: OrderRequest, onTables: Order[] = Order.onTables()): User | undefined {
        const owner = request.getTableOwner(onTables)
        return owner && owner.id !== User.current()?.id ? owner : undefined
    }

    /**
     * The owner whose go-ahead approving needs. Approving issues a round under
     * the approver's name onto a table the approver cannot open, so the hub
     * asks first — the same rule a table transfer onto another waiter's table
     * follows: nobody to ask on a free or own table, with allow_enter_all_tables,
     * or on a limited table.
     */
    function approvalConsentOwner(request: OrderRequest): User | undefined {
        const owner = otherTableWaiter(request)
        if (!owner) return undefined
        if (UserService.allows('allow_enter_all_tables')) return undefined
        if (request.getTable()?.type === 'limited') return undefined
        return owner
    }

    /**
     * Approve = claim on the backend (pending → approved), then issue a real
     * order through the SAME pipeline a waiter uses for an unpaid round —
     * fiscalization (ME), KDS ticket, kitchen slips, callout, table session —
     * and link it back to the request. On any failure the claim is reverted so
     * the request returns to the pending pool. Nothing is ever lost or deleted.
     */
    async function approve(request: OrderRequest, confirmForeignTable: ConfirmForeignTable) {
        // 0. Another waiter's table: ask first. Before the claim, so a declined
        //    approve leaves nothing to revert.
        const owner = approvalConsentOwner(request)
        if (owner && !(await confirmForeignTable(owner, request.getTableName()))) {
            return { success: false, error: 'cancelled' }
        }

        // 1. Claim (online-only — the claim is the exactly-once gate).
        const claim = await TableSessionService.approveOrderRequest(request.id)
        if (!claim.success) {
            if (claim.error === 'already_processed') {
                // Claimed/handled elsewhere — drop it from this hub. Benign:
                // the order exists (a peer issued it), so report success.
                request.status = 'approved'
                settleLocally(request)
                return { success: true }
            }
            return { success: false, error: claim.error }
        }

        // 2. Build the order from the request, exactly like a waiter round.
        const order = await Order.createNew()
        order.source = 'qr'

        let mappableCount = 0
        for (const reqItem of request.items) {
            if (reqItem.product_id === null) continue
            const product = Product.find(reqItem.product_id)
            if (!product) continue
            const posItem = order.addItem(product, Number(reqItem.count) || 0)
            if (posItem) {
                mappableCount++
                if (reqItem.comments.length) posItem.comments = [...reqItem.comments]
            }
        }

        const revert = async (error: string) => {
            await TableSessionService.finalizeOrderRequest(request.id, { revert: true })
            return { success: false, error }
        }

        if (mappableCount === 0) return revert('no_valid_items')

        // 3. Issue. `source` set ⇒ executePay skips callout allocation, so do it
        // here to match a waiter round's kitchen-slip callout.
        order.setFlag(OrderFlagType.calloutCode, OrderService.computeCalloutCode())
        const closed = order.close({
            table: Table.find(request.table_id),
            status: OrderStatusType.unpaid,
            payments: [],
            flag: `${OrderFlagType.type},${CustomPaymentTypeLabel.order}`,
        })

        // Durable link set before issue (offline-safe); order_id backfills below.
        request.order_hash = closed.hash

        // Lazy-load the order-processing pipeline. useProcessOrder reads the
        // Pinia store at module scope, so a STATIC import here would pull it into
        // the eager bootstrap graph (router → MainLayout → useOrdersHub →
        // useOrderRequests) and run useAppStore() before app.use(pinia) — blank
        // screen on launch. Importing on first approve() keeps it behind a
        // post-boot, post-Pinia boundary, matching how master loads it (only via
        // lazy route views, never the eagerly-imported shell). Do not hoist.
        const { useProcessOrder } = await import('@/composables/useProcessOrder')
        const { processReceipt } = useProcessOrder()

        const result = await processReceipt(closed)
        if (result.status !== 'success') {
            request.order_hash = null
            return revert('issue_failed')
        }

        // 4. Link the issued order to the request and settle locally. issue()
        // inserts the synced order (carrying the server id) under the same hash,
        // so read the id back from the store — `closed.id` stays null locally.
        request.order_id = Order.find(closed.hash)?.id ?? null
        request.status = 'approved'
        await TableSessionService.finalizeOrderRequest(request.id, {
            order_hash: request.order_hash,
            order_id: request.order_id,
        })
        settleLocally(request)
        return { success: true }
    }

    async function reject(request: OrderRequest, reason?: string) {
        const result = await TableSessionService.rejectOrderRequest(request.id, reason)
        if (result.success) {
            request.status = 'rejected'
            request.reject_reason = reason ?? null
            settleLocally(request)
        } else if (result.error === 'already_processed') {
            // Approved into an order elsewhere — drop from the pending view.
            request.status = 'approved'
            settleLocally(request)
            return { success: true }
        }
        return result
    }

    return {
        pendingOrderRequests,
        otherTableWaiter,
        approvalConsentOwner,
        approve,
        reject,
    }
}
